import { useCallback, useEffect } from 'react'
import { ConnectionState } from 'livekit-client'
import { useCallActions } from '@/utils'
import {
  ControlButtonsBar,
  DeviceSettingsModal,
  ConnectionStatus,
  ParticipantVideo,
  type ParticipantInfo
} from '@/pages/CallPage'


export function CallPage() {
  const {
    // State
    room,
    connectionState,
    isMicMuted,
    isCameraMuted,
    showDeviceSettings,
    selectedMicId,
    selectedCamId,
    selectedSpeakerId,
    deviceRefreshKey,

    // Actions
    connectToRoom,
    disconnectRoom,
    toggleMicrophone,
    toggleCamera,
    handleDeviceChange,
    pinParticipant,
    setShowDeviceSettings,

    // Derived state
    pinnedParticipantInfo,
    smallParticipants,
  } = useCallActions()

  // Layout is handled in MainLayout based on route; no DOM mutation needed

  // Connect to room on component mount
  useEffect(() => {
    connectToRoom()
    return () => {
      if (room) {
        console.log('Disconnecting room on component unmount')
        room.disconnect()
      }
    }
  }, []) // Remove connectToRoom from dependencies to prevent multiple calls

  // End call handler
  const endCall = useCallback(() => {
    disconnectRoom()
  }, [disconnectRoom])

  // Render participant video using the ParticipantVideo component
  const renderParticipantVideo = useCallback((
    participantInfo: ParticipantInfo,
    isPinned: boolean = false,
    showControls: boolean = true
  ) => {
    const isLocal = participantInfo.participant === room?.localParticipant

    return (
      <ParticipantVideo
        key={participantInfo.participant.identity}
        participantInfo={participantInfo}
        variant={isPinned ? 'pinned' : 'small'}
        showControls={showControls}
        onClick={() => pinParticipant(participantInfo.participant.identity)}
        isLocalParticipant={isLocal}
      />
    )
  }, [room, pinParticipant])

  return (
    <div className="w-screen min-h-dvh flex flex-col bg-black overflow-hidden relative">

      {/* Connection Status */}
      <ConnectionStatus state={connectionState} />

      {/* Main video area */}
      <div
        className={`flex-1 min-h-0 flex items-center justify-center p-4 box-border`}
      >
        <div className="w-full h-full relative rounded-[12px] overflow-hidden">
          {pinnedParticipantInfo ? (
            renderParticipantVideo(pinnedParticipantInfo, true)
          ) : (
            <div className="flex items-center justify-center text-white text-2xl">
              {connectionState === ConnectionState.Connecting ? 'Connecting...' : 'No participants'}
            </div>
          )}
        </div>
      </div>

      {/* Small participants row */}
      {smallParticipants.length > 0 && (
        <div className="participants-row px-4 py-2 flex gap-2 overflow-x-auto max-h-[110px]">
          {smallParticipants.map(participantInfo =>
            renderParticipantVideo(participantInfo, false)
          )}
        </div>
      )}

      {/* Control buttons */}
      <ControlButtonsBar
        connectionState={connectionState}
        isMicMuted={isMicMuted}
        isCameraMuted={isCameraMuted}
        toggleMicrophone={toggleMicrophone}
        toggleCamera={toggleCamera}
        onDeviceSettings={() => setShowDeviceSettings(true)}
        onEndCall={endCall}
      />

      {/* Device Settings Modal */}
      <DeviceSettingsModal
        open={showDeviceSettings}
        onClose={() => setShowDeviceSettings(false)}
        selectedMicId={selectedMicId}
        selectedCamId={selectedCamId}
        selectedSpeakerId={selectedSpeakerId}
        onChangeMic={(e) => handleDeviceChange(e.target.value, 'audioinput')}
        onChangeCam={(e) => handleDeviceChange(e.target.value, 'videoinput')}
        onChangeSpeaker={(e) => handleDeviceChange(e.target.value, 'audiooutput')}
        refreshKey={deviceRefreshKey}
      />
    </div>
  )
}
